/**
 * MENU PAR TÂCHES (L-NAV-2, navigation validée par le client le 2026-10-14).
 *
 * Le menu n'est plus rangé par SYSTÈME (« Analytics », « CRM », « Journaux »,
 * « Administration », « Compte », « Développement ») mais par TÂCHE, à plat :
 *
 *   agent       Téléphone · Contacts · Historique · Mon activité │ Paramètres
 *   admin       Aujourd'hui · Téléphone · Contacts · Campagnes · Historique ·
 *               Analyse │ Équipe · Modèles · Paramètres
 *               (L-NAV-7 : « Équipe » = /equipe, sans sous-entrée)
 *   super-admin les 9 de l'admin + un seul groupe repliable « Système »
 *               (Clients · Numéros Twilio · Fournisseurs ; « Outils MCP »
 *               retiré le 2026-10-15, #296)
 *
 * Ce module ne fait que DÉCRIRE le menu ; `Sidebar.tsx` le dessine.
 *
 * ── DROITS : UNE PERMISSION PAR ÉCRAN (L-NAV-9) ─────────────────────────────
 * La condition de chaque entrée vient du catalogue `access/screens.ts`, le
 * MÊME que lisent les routes (`<ProtectedRoute screen>`) : menu et route ne
 * peuvent plus diverger. L'ancienne condition de SECTION (`ADMIN_SECTION` =
 * canAccessUsers ‖ canManagePermissions ‖ canAccessTenants ‖ canAccessTwilio)
 * est retirée : elle était toujours impliquée par la condition de l'entrée
 * (canAccessUsers ⇒ section, canManagePermissions ⇒ section, super-admin ⇒
 * section). Preuves : `Sidebar.navigation.spec.tsx` (préréglages du code) et
 * `Sidebar.prodProfiles.spec.tsx` (profils du relevé de prod) — instantanés
 * identiques avant/après.
 *
 * ── PONTS ──────────────────────────────────────────────────────────────────
 * Les fusions de pages (Historique unique, Équipe, Aujourd'hui…) viendront dans
 * des lots ultérieurs. En attendant, une rangée de la cible qui regroupe
 * plusieurs pages ACTUELLES est un « pont » : elle mène à la première page du
 * groupe, et les autres pages apparaissent en sous-entrées SOUS la rangée,
 * seulement quand on est dans cette rubrique (une tape → la page ET ses
 * voisines ; aucun chevron, aucun accordéon). Si la première page n'est pas
 * permise, les autres remontent en rangées autonomes : rien ne devient
 * orphelin.
 */
import type { LucideIcon } from 'lucide-react'
import type { PermissionKey } from '../types/permissions'
import { canAccessScreen, type ScreenId } from '../access/screens'
import {
  BarChart3,
  Building2,
  Download,
  History,
  Home,
  Megaphone,
  MessageSquare,
  Phone,
  PhoneForwarded,
  Server,
  Settings,
  UserCog,
  Users,
  Wrench,
} from 'lucide-react'

export interface NavLink {
  /** Clé i18n du libellé (namespace `shell`) — jamais une chaîne affichée. */
  labelKey: string
  href: string
  icon: LucideIcon
  /**
   * Autres chemins où l'entrée reste marquée active : un chemin exact, ou un
   * préfixe terminé par `/` (fiche d'un contact, d'une campagne, d'un client).
   * `'/'` seul est un chemin EXACT (la racine), jamais un préfixe.
   */
  also?: string[]
}

/** Une rangée du menu ; `children` = sous-entrées d'un pont. */
export interface NavRow extends NavLink {
  children: NavLink[]
}

export interface NavModel {
  /** Le travail : appeler, chercher, revoir, mesurer. */
  work: NavRow[]
  /** L'organisation, sous un séparateur : équipe, contenus, réglages. */
  organisation: NavRow[]
  /** Super-admin seulement : un groupe repliable, replié par défaut. */
  system: NavLink[]
}

export interface NavContext {
  hasPermission: (permission: PermissionKey) => boolean
  isSystemAdmin: boolean
  isAgent: boolean
  role: string | undefined
  /** AlloDons raccordé (faux pendant la lecture : pas de clignotement). */
  allodonsEnabled: boolean
}

interface Candidate {
  visible: boolean
  link: NavLink
}

const entry = (visible: boolean, link: NavLink): Candidate => ({ visible, link })

/**
 * Un pont de la cible : la PREMIÈRE page porte le libellé et l'icône de la
 * rangée (« Historique », « Équipe »…) ; ses voisines permises deviennent des
 * sous-entrées. Si la première page n'est pas permise, les voisines remontent
 * en rangées autonomes.
 */
function group(primary: Candidate, ...others: Candidate[]): NavRow[] {
  const rest = others.filter((c) => c.visible).map((c) => c.link)
  if (primary.visible) return [{ ...primary.link, children: rest }]
  return rest.map((link) => ({ ...link, children: [] }))
}

const single = (candidate: Candidate): NavRow[] =>
  candidate.visible ? [{ ...candidate.link, children: [] }] : []

export function buildNavModel(ctx: NavContext): NavModel {
  // L-NAV-6 — `allodonsEnabled` n'est plus lu (l'entrée « Donateurs » est
  // retirée) ; le champ reste dans `NavContext` pour les entrées à venir.
  const { hasPermission, isSystemAdmin, isAgent, role } = ctx

  // L-NAV-9 — la condition d'un écran vient du catalogue, comme à la route.
  const allowed = (screen: ScreenId) => canAccessScreen(screen, { role, isSystemAdmin, hasPermission })

  // L-NAV-8 — « Aujourd'hui » n'est plus un pont : c'est la page `/accueil`
  // (ex-/supervision), accueil de l'admin — la racine `/` y atterrit, l'entrée
  // y reste donc marquée (et « Analyse » ne l'est plus sur `/`).
  // Intégration 3B : la condition vient du catalogue (écran `today` =
  // canAccessStats ∧ ADMIN, compte système partout — L-NAV-9).
  const todayVisible = allowed('today')
  // fin L-NAV-8
  const work: NavRow[] = [
    ...single(entry(todayVisible, { labelKey: 'nav.items.today', href: '/accueil', icon: Home, also: ['/'] })),
    // LOT-SUNSET-2 : la scène v2 est la SEULE surface d'appel.
    ...single(entry(allowed('softphone'), { labelKey: 'nav.items.phone', href: '/v2/softphone', icon: Phone })),
    // Pont : Contacts + Import (#208 : sous `canAccessImport` seulement).
    // L-NAV-6 — plus d'entrée « Donateurs » : les donateurs sans fiche sont un
    // onglet de Contacts (`/contacts?tab=donors`, AlloDons raccordé) et
    // `/donations/history` y redirige.
    ...group(
      entry(allowed('contacts'), { labelKey: 'nav.items.contacts', href: '/contacts', icon: Users, also: ['/contacts/'] }),
      entry(allowed('import'), { labelKey: 'nav.items.import', href: '/import', icon: Download, also: ['/import/'] }),
    ),
    ...single(
      entry(allowed('campaigns'), {
        labelKey: 'nav.items.campaigns',
        href: '/campaigns',
        icon: Megaphone,
        // L-NAV-3 : plus de '/admin/campaigns/' — ces pages sont devenues des
        // onglets de la fiche `/campaigns/:id` (redirections).
        also: ['/campaigns/'],
      }),
    ),
    // #451 (planche 15) — l'AGENT n'a pas la liste `/campaigns` : son entrée
    // « Campagnes » mène à « Mes campagnes » (campagnes où il est affecté),
    // d'où il ouvre la fiche en lecture seule (`/campaigns/:id`, écran
    // `campaignDetail`, sans entrée de menu propre).
    ...single(
      entry(!allowed('campaigns') && allowed('myCampaigns'), {
        labelKey: 'nav.items.campaigns',
        href: '/mes-campagnes',
        icon: Megaphone,
        also: ['/campaigns/'],
      }),
    ),
    // L-NAV-5 — « Historique » est UNE page (`/historique`) à 4 onglets :
    // plus de pont ni de sous-entrées. L-NAV-9 : la rangée lit canAccessCalls ;
    // chaque onglet lit SON drapeau dans `pages/history/historyTabs.ts`.
    ...single(entry(allowed('history'), { labelKey: 'nav.items.history', href: '/historique', icon: History })),
    // fin L-NAV-5
    // L'agent y lit SES chiffres : « Mon activité ». `/` atterrit sur
    // /analytics pour les rôles non-agent sans « Aujourd'hui » (RoleLanding ;
    // L-NAV-8 : l'admin atterrit sur /accueil). L-NAV-9 : canAccessStats seul.
    ...single(
      entry(allowed('analytics'), {
        labelKey: isAgent ? 'nav.items.myActivity' : 'nav.items.analyse',
        href: '/analytics',
        icon: BarChart3,
        also: todayVisible ? [] : ['/'],
      }),
    ),
  ]

  const organisation: NavRow[] = [
    // L-NAV-7 — « Équipe » est UNE page (`/equipe`, liste + fiche
    // `/equipe/:userId`) : plus de pont ni de sous-entrée « Permissions » —
    // les accès se règlent sur la fiche de chaque personne (section « Accès »)
    // et `/admin/permissions` redirige vers `/equipe`.
    // L-NAV-9 × L-NAV-7 (intégration 3B) : la condition vit dans le catalogue
    // `access/screens.ts` (écran `team`) = l'UNION des deux anciennes entrées
    // (canAccessUsers ‖ canManagePermissions ; compte système partout) — rien
    // ne devient orphelin.
    ...single(
      entry(allowed('team'), { labelKey: 'nav.items.team', href: '/equipe', icon: UserCog, also: ['/equipe/'] }),
    ),
    // fin L-NAV-7
    // L-NAV-9 : Modèles = canAccessSmsTemplates ‖ canAccessEmailTemplates
    // (compat : canManagePermissions les vaut encore, cf. access/legacyFallbacks).
    ...single(
      entry(allowed('templates'), {
        labelKey: 'nav.items.templates',
        href: '/messaging/templates',
        icon: MessageSquare,
      }),
    ),
    // L-NAV-4 — Paramètres pour tous, UNE seule entrée : Intégrations et
    // AlloDons sont des sections de la console (/settings?section=…), portées
    // par son rail, et les anciennes adresses /settings/* y redirigent.
    ...single(entry(allowed('settings'), { labelKey: 'nav.items.settings', href: '/settings', icon: Settings, also: ['/settings/'] })),
    // fin L-NAV-4
  ]

  // L-NAV-9 : groupe « Système » = compte système, sans autre condition.
  const system: NavLink[] = [
    entry(allowed('tenants'), { labelKey: 'nav.items.clients', href: '/admin/tenants', icon: Building2, also: ['/admin/tenants/'] }),
    entry(allowed('twilioNumbers'), { labelKey: 'nav.items.twilioNumbers', href: '/admin/twilio-numbers', icon: PhoneForwarded }),
    entry(allowed('providers'), { labelKey: 'nav.items.providers', href: '/messaging/providers', icon: Wrench }),
  ]
    .filter((c) => c.visible)
    .map((c) => c.link)

  return { work, organisation, system }
}

/** Icône du groupe « Système ». */
export const SYSTEM_ICON = Server

/** L'entrée correspond-elle à la page ouverte ? */
export function isLinkActive(link: NavLink, pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  if (path === link.href) return true
  // Un alias terminé par `/` est un PRÉFIXE (`/contacts/` couvre les fiches) —
  // sauf la racine : `'/'` préfixerait TOUT et allumerait l'entrée sur chaque
  // page (défaut relevé en recette banc). La racine se compare à l'égalité.
  return (link.also ?? []).some((other) =>
    other.length > 1 && other.endsWith('/') ? path.startsWith(other) : path === other,
  )
}
